/**
 * RESEARCH CONTROL — WHAT SCOUT IS AUTHORIZED TO DO, WHAT IT IS ACTUALLY DOING,
 * AND THE CANCEL THAT IS REAL.
 *
 * ---------------------------------------------------------------- THE RULES
 *
 *   1. THIS IS A READER AND A CANCELLER, NOT A LAUNCHER. There is no "Start
 *      Research" form anywhere in this product, deliberately: a window opens
 *      when the Owner SAYS SO IN THE MAIN CHAT, through the engine's own
 *      admission path. A form here would be a second way to authorize research,
 *      and the second way is the one that drifts from the first. The screen says
 *      so where a form would be.
 *
 *   2. IT NEVER SHOWS RUNNING BECAUSE SOMETHING IS ANIMATING. `RUNNING` is a
 *      value in a row. A window whose deadline has passed while no tick has run
 *      yet is shown as EXACTLY THAT — the clock ran out and nothing has observed
 *      it — rather than as still running, and never as COMPLETED on the strength
 *      of a timer reaching zero.
 *
 *   3. IT DOES NOT INVENT THE STATES THE BRIEF LISTS. Ten names were asked for;
 *      the backend persists four states plus three other durable facts. The
 *      difference is rendered rather than papered over — see the vocabulary
 *      panel, which says which names exist on the record and which are this
 *      screen declining to answer.
 *
 *   4. A QUIET RUN IS SHOWN AS QUIET. There is no progress bar and no
 *      percentage. What is shown is the segment count, when the last segment
 *      was, and whether that is recent enough to call the lane alive — and the
 *      threshold used for that judgement is this screen's own and is stated.
 *
 *   5. IT DOES NOT REFRESH IN A WAY THAT COSTS ANYTHING. Every read here is a
 *      GET that runs no model. The poll is 30 seconds, only while the tab is
 *      VISIBLE, and it stops when the tab is hidden — a background tab polling a
 *      research lane is a probe storm nobody is reading.
 *
 * AND THE CANCEL IS THE SAME CANCEL THE CHAT USES. One button, one route, one
 * governed mechanism: `POST /gateway/research/runs/:runId/cancel` records the
 * Owner's stop, and the next tick ends the window through the path the deadline
 * takes. The screen does NOT say the window stopped when the button is pressed,
 * because at that instant it has not.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createLoadable } from '../resource';
import { readGateway, type GatewayError } from '../gateway';
import { Failure } from './failure';
import { navigate, route } from '../router';
import { pathForDestination } from '../paths';
// THE CADENCE MARGIN AND THE STATE DERIVATION ARE SHARED WITH THE ACTIVITY MARK
// — see the block comment where they used to live, below the record types.
import { DISPLAY_CAPTIONS, QUIET_AFTER_MS, displayStateOf, mostSevere, runActivity } from '../activity';
import { ActivityMark } from './activity-mark';
import { PageHeader, Technical } from './about';
import { Markdown } from '../markdown';
import { formatTurnTime as formatRecordTime } from '../chat';

/**
 * THE PATH SEGMENT THAT NAMES ONE AUTHORIZED WINDOW.
 *
 * `/research/window/<runId>` — the same three-segment shape `/chat/discuss/<ref>`
 * and `/experiments/receipt/<id>` already use, so a link to a record means the
 * same kind of thing wherever it appears.
 */
export const WINDOW_SECTION = 'window';

/* ------------------------------------------------------------- the records */

interface ResearchRun {
	runId: string;
	conversationId: string | null;
	state: string;
	authorizationText: string;
	requestedMs: number;
	deadlineAt: string;
	startedAt: string;
	endedAt: string | null;
	endReason: string | null;
	terminalReason: string | null;
	segmentsRun: number;
	unrecoverableSegments: number;
	drySegments: number;
	cancelRequested: boolean;
	lastSegmentAt: string | null;
	reportRef: string | null;
	/** Rendered by the gateway for the newest few ENDED windows; null on a live
	 *  one, and null on an old one whose report was not in this read. */
	report: string | null;
}

interface RunsPayload {
	generatedAt: string;
	wired: boolean;
	reason?: string;
	runs: ResearchRun[];
}

interface Episode {
	episodeId: string;
	targetKey: string;
	question: string;
	status: string;
	createdAt: string;
	updatedAt: string;
	sourceCount?: number;
	findingCount?: number;
}

/**
 * ONE ENTRY OF SCOUT'S OWN RESEARCH JOURNAL.
 *
 * `author` is the difference the Owner asked for, and it is a stored field
 * rather than a guess from the prose. A row written by the model at Scout's
 * request has author `SCOUT`; a deterministic note the Harness writes when the
 * window ended before a synthesis has author `HARNESS`. The card labels them
 * differently and never presents the second as the first.
 */
interface JournalEntry {
	journalId: string;
	kind: 'OPENING' | 'PROGRESS' | 'CLOSING' | 'CONCLUSION';
	title: string | null;
	content: string;
	status: 'AUTHORED' | 'FAILED' | 'INCOMPLETE';
	statusNote: string | null;
	author: 'SCOUT' | 'HARNESS';
	evidenceRefs: string[];
	createdAt: string;
}

interface JournalPayload {
	wired: boolean;
	reason?: string;
	runId: string;
	entries: JournalEntry[];
}

/** A RESEARCH TOPIC — the subject a page is built around. It is not a window. */
interface ResearchTopic {
	topicId: string;
	title: string;
	objective: string;
	summary: string;
	status: string;
	createdAt: string;
	updatedAt: string;
	lastActivityAt: string;
}

interface TopicsPayload {
	wired: boolean;
	reason?: string;
	topics: ResearchTopic[];
	offset?: number;
	limit?: number;
	hasMore?: boolean;
}

/** THE DRIVER'S READ-ONLY TELEMETRY. Every field is optional because an older
 *  deployment may answer a shorter shape; absence is rendered as "not
 *  observed", never as a healthy driver. */
interface ResearchDriverSnapshot {
	available?: boolean;
	reason?: string;
	driverImplemented?: boolean;
	scheduledHandlerImplemented?: boolean;
	schedulerBindingPresent?: boolean;
	cronTriggerObservedAt?: string | null;
	nextAlarmAt?: string | null;
	lastAlarmTickAt?: string | null;
	alarm?: {
		known: boolean;
		ownerBound: boolean;
		armedFor: string | null;
		lastDecision: string | null;
		lastReason: string | null;
		lastTickAt: string | null;
	} | null;
	activeRun?: {
		runId: string;
		state: string;
		deadlineAt: string;
		segmentsRun: number;
		lastSegmentAt: string | null;
		cancelRequested: boolean;
	} | null;
	progressBlocked?: boolean;
	blockedReason?: string | null;
	monitoringAvailable?: boolean;
	levels?: {
		driverImplemented: boolean;
		triggerObserved: boolean;
		alarmScheduled: boolean;
		tickExecuted: boolean;
		segmentAdvanced: boolean;
	};
	lastTicks?: Array<{
		tickId: string;
		origin: string;
		decision: string;
		reason: string;
		at: string;
		modelInvocations: number;
	}>;
	note?: string;
}

interface ResearchStatusPayload {
	driver?: ResearchDriverSnapshot;
}

/**
 * THE WINDOW-STATE DERIVATION MOVED TO `../activity`, AND THIS FILE IMPORTS IT.
 *
 * It lived here first. Slice 10 gave the Main Chat an activity mark that has to
 * answer the same question — "is this window's lane alive" — and two screens
 * with two copies of a five-minute margin would eventually disagree about one
 * window, with no way for the Owner to tell which was lying. So the margin, the
 * derivation and its captions are now one module, and BOTH screens read it.
 *
 * `QUIET_AFTER_MS` and `DISPLAY_CAPTIONS` are re-exported through the import so
 * nothing else in this file changed shape.
 */

/** How often the lane is re-read while the tab is visible. */
const POLL_MS = 30_000;

/* ------------------------------------------------------------ the vocabulary */

/**
 * THE TEN NAMES THE BRIEF ASKED FOR, MAPPED ONTO WHAT IS ACTUALLY STORED.
 *
 * THIS TABLE IS THE HONESTY OF THE SCREEN. `scout_research_runs` persists
 * `state` (RUNNING | COMPLETED | STOPPED | FAILED), `cancel_requested`,
 * `deadline_at`, `last_segment_at` and `end_reason`. It does NOT persist an
 * "authorized but not started" state, a queue position, or an "awaiting"
 * anything — because a window is RUNNING from the instant it is opened, and
 * inventing those names in the UI would be inventing persisted state for
 * display, which the brief forbids and which would be a lie about the record.
 *
 * So three of the ten are reported as NOT EXISTING, and they are reported
 * LOUDLY RATHER THAN OMITTED: a reader who was promised a queue and finds no
 * queue must be told there is none, not left to conclude the window is missing.
 */
const STATE_VOCABULARY: ReadonlyArray<{
	name: string;
	stored: string;
}> = [
	{ name: 'AUTHORIZED', stored: 'NOT A STORED STATE — a window is RUNNING from the moment it opens. This product has no authorized-but-not-started phase.' },
	{ name: 'QUEUED', stored: 'NOT A STORED STATE — there is no queue. A window opens on the Owner’s instruction and its first segment is served by the next tick.' },
	{ name: 'RUNNING', stored: 'STORED — `state = RUNNING`, with the deadline still ahead.' },
	{ name: 'AWAITING', stored: 'NOT A STORED STATE — a window between segments is still RUNNING. How long it has been quiet is `lastSegmentAt`, shown below.' },
	{ name: 'CANCELLING', stored: 'DERIVED — `state = RUNNING` AND `cancelRequested = true`: the Owner’s stop is recorded and the window has not yet been ended by a tick.' },
	{ name: 'CANCELLED', stored: 'DERIVED — `state = STOPPED` with `endReason = OWNER_CANCELLED` (the Owner’s stop) or `BUDGET_EXHAUSTED` (the authorized spend envelope).' },
	{ name: 'COMPLETED', stored: 'DERIVED — `state = COMPLETED`, reached by `WINDOW_ENDED` or Scout’s own conclusion.' },
	{ name: 'DEADLINE_REACHED', stored: 'DERIVED — the same record as COMPLETED when the authorized clock ran out.' },
	{ name: 'FAILED', stored: 'DERIVED — `state = FAILED`, reached by `MACHINERY_UNRECOVERABLE`, `PROVIDER_UNAVAILABLE` or `RECOVERY_EXHAUSTED`.' },
	{ name: 'UNKNOWN', stored: 'NOT A STORED STATE — it is what this screen shows when it could not read the record at all.' },
];

function minutes(ms: number): string {
	return `${Math.round(ms / 60_000)} min`;
}

function remainingText(deadlineAt: string, observedAtMs: number): string {
	const left = Date.parse(deadlineAt) - observedAtMs;
	if (left <= 0) return '0 min — the authorized window has ended';
	return `${Math.ceil(left / 60_000)} min remaining of the authorized window`;
}

export function ResearchView({ mode = 'control' }: { mode?: 'control' | 'windows' } = {}) {
	const runs = useMemo(
		() => createLoadable<RunsPayload>(() => readGateway<RunsPayload>('/api/scout/gateway/research/runs')),
		[],
	);
	// THE DIRECTORY IS THE PRIMARY READ. Windows remain below it as the
	// authorization history; a window is not a subject.
	//
	// SEARCH AND STATUS ARE THE SERVER'S (H-015/H-036). They used to filter the
	// loaded page in the browser, so "no match" meant "not in the first hundred"
	// — a filter over an arbitrary page presented as a filter over the corpus.
	// The route has carried `q` and `status` all along; the read now sends them.
	const [topicQuery, setTopicQuery] = useState('');
	const [topicStatus, setTopicStatus] = useState('ALL');
	const topics = useMemo(() => {
		const params = new URLSearchParams({ limit: '100' });
		const query = topicQuery.trim();
		if (query.length > 0) params.set('q', query);
		if (topicStatus !== 'ALL') params.set('status', topicStatus);
		return createLoadable<TopicsPayload>(() =>
			readGateway<TopicsPayload>(`/api/scout/gateway/research/topics?${params.toString()}`),
		);
	}, [topicQuery, topicStatus]);
	const [topicsOlder, setTopicsOlder] = useState<ResearchTopic[]>([]);
	const [topicsMore, setTopicsMore] = useState(false);
	const [topicsPaging, setTopicsPaging] = useState(false);
	const [topicsPageError, setTopicsPageError] = useState<GatewayError | null>(null);
	// THE DRIVER IS A READ, NOT AN INFERENCE. It is the same trusted status
	// route the CLI uses; this screen renders what the Deployment reported.
	const driver = useMemo(
		() =>
			createLoadable<ResearchStatusPayload>(() =>
				readGateway<ResearchStatusPayload>('/api/scout/gateway/research/status'),
			),
		[],
	);
	/** Which window's work is expanded, and what was read for it. */
	const [openRunId, setOpenRunId] = useState<string | null>(null);
	const [episodes, setEpisodes] = useState<Episode[] | null>(null);
	const [episodeError, setEpisodeError] = useState<GatewayError | null>(null);
	/** THE RUN'S OWN CONTINUATION. A continuous window can open more episodes
	 *  than the first twenty-record card read; the episodes route already pages
	 *  by offset, and this is the control that uses it. */
	const [episodesMore, setEpisodesMore] = useState(false);
	const [episodesPaging, setEpisodesPaging] = useState(false);
	const [episodesPageError, setEpisodesPageError] = useState<GatewayError | null>(null);
	/** SCOUT'S OWN WORDS FOR THE OPENED WINDOW, read beside the records. */
	const [journal, setJournal] = useState<JournalEntry[] | null>(null);
	const [journalError, setJournalError] = useState<GatewayError | null>(null);
	/** The cancel's own state, so a press is a REQUEST with an outcome rather
	 *  than an optimistic tick. */
	const [cancelError, setCancelError] = useState<{ runId: string; error: GatewayError } | null>(null);
	const [cancelSent, setCancelSent] = useState<Record<string, true>>({});

	const visible = useRef(true);

	useEffect(() => {
		setTopicsOlder([]);
		setTopicsMore(false);
		setTopicsPageError(null);
		void topics.load();
	}, [topics]);

	// THE POLL IS VISIBILITY-BOUNDED, AND THAT IS THE WHOLE DESIGN. A research
	// lane polled from a background tab is a probe storm nobody is reading; a
	// screen that stopped updating while the Owner watched would be worse. So it
	// refreshes on the interval ONLY while the document is visible, and it reads
	// once immediately when the tab becomes visible again — which is the moment
	// the Owner's information is most stale.
	useEffect(() => {
		void runs.load();
		void topics.load();
		void driver.load();
		const onVisibility = (): void => {
			visible.current = document.visibilityState === 'visible';
			if (visible.current) {
				void runs.load();
				void topics.load();
				void driver.load();
			}
		};
		document.addEventListener('visibilitychange', onVisibility);
		const timer = window.setInterval(() => {
			if (visible.current) {
				void runs.load();
				void topics.load();
				void driver.load();
			}
		}, POLL_MS);
		return () => {
			document.removeEventListener('visibilitychange', onVisibility);
			window.clearInterval(timer);
		};
	}, [runs, topics, driver]);

	const state = runs.state.value;
	const data = state.data;
	// EVERY AGE ON THIS SCREEN IS MEASURED AGAINST WHEN THE SERVER ANSWERED, not
	// against the browser's clock. A device whose clock is a minute out would
	// otherwise report a deadline that had passed as still ahead — and the
	// countdown is the one number here an Owner might act on.
	const observedAtMs = data === null ? Date.now() : Date.parse(data.generatedAt);
	const allRuns = data?.runs ?? [];
	const live = allRuns.filter((run) => run.state === 'RUNNING');
	const ended = allRuns.filter((run) => run.state !== 'RUNNING');
	const topicsState = topics.state.value;
	// THE FIRST PAGE WINS WHEN A POLITENESS POLL RE-READS IT, and the appended
	// tail is deduplicated by id: the poll replaces page one while the Owner's
	// Load more pages stay below it, so a topic touched since the read cannot
	// appear twice.
	const firstTopics = topicsState.data?.topics ?? [];
	const seenTopics = new Set(firstTopics.map((topic) => topic.topicId));
	const topicList = [
		...firstTopics,
		...topicsOlder.filter((topic) => !seenTopics.has(topic.topicId)),
	];
	const topicsHasMore =
		topicsMore || (topicsOlder.length === 0 && topicsState.data?.hasMore === true);
	const topicStatuses = [...new Set(topicList.map((topic) => topic.status))].sort();
	const loadMoreTopics = useCallback(async () => {
		if (topicsState.status !== 'ready' || topicsState.data === null) return;
		setTopicsPaging(true);
		setTopicsPageError(null);
		const params = new URLSearchParams({
			limit: '100',
			offset: String(firstTopics.length + topicsOlder.length),
		});
		const query = topicQuery.trim();
		if (query.length > 0) params.set('q', query);
		if (topicStatus !== 'ALL') params.set('status', topicStatus);
		const result = await readGateway<TopicsPayload>(
			`/api/scout/gateway/research/topics?${params.toString()}`,
		);
		setTopicsPaging(false);
		if (!result.ok) {
			setTopicsPageError(result.error);
			return;
		}
		setTopicsOlder((previous) => [...previous, ...result.data.topics]);
		setTopicsMore(result.data.hasMore === true);
	}, [topicsState, firstTopics.length, topicsOlder.length, topicQuery, topicStatus]);
	const driverRead = driver.state.value;
	const driverSnapshot = driverRead.data?.driver ?? null;

	/**
	 * ONE READING FOR THE WHOLE SCREEN, FROM ALL OF THEM.
	 *
	 * THIS IS WHAT `mostSevere` IS FOR, AND UNTIL THIS EXISTED IT HAD NO CALLER.
	 * A screen listing five windows has to answer "is anything happening"
	 * somewhere, and the wrong answer is the one a plain loop gives: whichever
	 * window happens to be last. The ranking puts a FAILED or an UNKNOWN above
	 * anything that is moving, so a window that could not start is never hidden
	 * behind one that is ticking — which on a screen an Owner opens to decide
	 * whether to intervene is the only ordering that is defensible.
	 *
	 * EMPTY IS `IDLE`, AND THAT IS CORRECT HERE RATHER THAN A FALLBACK: a
	 * deployment with no windows has nothing outstanding, which is exactly what
	 * IDLE says.
	 */
	const overall = mostSevere(allRuns.map((run) => runActivity(run, observedAtMs)));

	const openRun = useCallback(
		async (runId: string) => {
			if (openRunId === runId) {
				setOpenRunId(null);
				setEpisodes(null);
				setJournal(null);
				return;
			}
			setOpenRunId(runId);
			setEpisodes(null);
			setEpisodeError(null);
			setEpisodesMore(false);
			setEpisodesPaging(false);
			setEpisodesPageError(null);
			setJournal(null);
			setJournalError(null);
			// BOTH READS ARE THE SAME ACT: opening a window asks what Scout
			// wrote AND what the record holds. A failed journal read does not
			// hide the records, and vice versa.
			const [records, authored] = await Promise.all([
				readGateway<{ episodes: Episode[]; hasMore?: boolean }>(
					`/api/scout/gateway/research/episodes?runId=${encodeURIComponent(runId)}&limit=20`,
				),
				readGateway<JournalPayload>(
					`/api/scout/gateway/research/runs/${encodeURIComponent(runId)}/journal`,
				),
			]);
			if (!records.ok) {
				setEpisodeError(records.error);
			} else {
				setEpisodes(records.data.episodes);
				setEpisodesMore(records.data.hasMore === true);
			}
			if (!authored.ok) {
				setJournalError(authored.error);
			} else {
				// A PAYLOAD MISSING ITS COLLECTION IS AN EMPTY ONE, NOT A CRASH.
				// The route validates its own shape; a stub or an older deployment
				// that answers `{}` must render the honest empty state rather than
				// taking down the page.
				setJournal(Array.isArray(authored.data.entries) ? authored.data.entries : []);
			}
		},
		[openRunId],
	);

	// A DEEP LINK OPENS THE WINDOW IT NAMES — `/research/window/<runId>`.
	//
	// SEARCH FINDS A WINDOW BY THE OWNER'S OWN WORDS ("what did I authorize in
	// June") and the record it points at is this card, expanded. The route is
	// what makes that a place rather than a state: a reload, a bookmark and a
	// second tab all land on the same expanded window.
	//
	// IT DOES NOT TOGGLE. `openRun` closes a window that is already open, which
	// is right for a press and wrong for a link — arriving twice must not close
	// what you asked to see. So the effect only opens.
	useEffect(() => {
		if (route.value.destination !== 'research' || route.value.section !== WINDOW_SECTION) return;
		const linked = route.value.recordId;
		if (linked === null || linked === openRunId) return;
		void openRun(linked);
	}, [route.value.section, route.value.recordId, openRunId, openRun]);

	const loadMoreRunEpisodes = useCallback(
		async (runId: string) => {
			if (episodes === null) return;
			setEpisodesPaging(true);
			setEpisodesPageError(null);
			const result = await readGateway<{ episodes: Episode[]; hasMore?: boolean }>(
				`/api/scout/gateway/research/episodes?runId=${encodeURIComponent(runId)}&limit=20&offset=${episodes.length}`,
			);
			setEpisodesPaging(false);
			if (!result.ok) {
				setEpisodesPageError(result.error);
				return;
			}
			setEpisodes((previous) => [...(previous ?? []), ...result.data.episodes]);
			setEpisodesMore(result.data.hasMore === true);
		},
		[episodes],
	);

	const cancel = useCallback(async (runId: string) => {
		setCancelError(null);
		const result = await readGateway<{ cancelled: boolean; reason?: string }>(
			`/api/scout/gateway/research/runs/${encodeURIComponent(runId)}/cancel`,
			{ method: 'POST', body: {} },
		);
		if (!result.ok) {
			// THE SCREEN DOES NOT PRETEND. A stop that was not recorded leaves no
			// trace here, and saying otherwise would have the Owner walk away
			// believing a window is ending.
			setCancelError({ runId, error: result.error });
			return;
		}
		setCancelSent((previous) => ({ ...previous, [runId]: true }));
		// RE-READ RATHER THAN ASSUME. The row now carries `cancelRequested`, and
		// the screen shows the state the RECORD holds rather than the one the
		// button implied.
		await runs.load();
	}, [runs]);

	return (
		<section class="view" aria-labelledby="research-heading">
			<PageHeader
				id="research-heading"
				title={mode === 'windows' ? 'Research windows' : 'Research'}
				about={{
					purpose:
						'The research subjects Scout is working on — each with one complete page — and the windows you authorized.',
					how: (
						<>
							<p>
								<strong>Research is organized by topic, not by window.</strong> A topic is a subject
								Scout is trying to understand; it can receive work across several authorized
								windows. Opening a topic shows its journal, knowledge, experiments, evidence,
								hypotheses, improvements and open questions on one page. The windows below are the
								authorization and execution history, and they remain readable.
							</p>
							<p>
								<strong>There is no start button here, and that is deliberate.</strong> A research
								window opens when you say so in the{' '}
								<a
									href={pathForDestination('chat')}
									onClick={(event) => {
										event.preventDefault();
										navigate({ destination: 'chat', section: null, recordId: null });
									}}
								>
									Main Chat
								</a>
								, through the engine’s own admission path. A form here would be a second way to
								authorize unattended research.
							</p>
							<p>
								Say what you want and for how long — for example <em>“Scout, start autonomous
								research for 12 hours.”</em> The duration is read from your own words, checked
								against what this deployment allows, and recorded verbatim as the authorization.
							</p>
							<p>
								The stored window states are <code>RUNNING</code>, <code>COMPLETED</code>,{' '}
								<code>STOPPED</code> and <code>FAILED</code>, plus whether a stop is recorded and
								when the last segment ran. A window whose deadline has passed with no end write
								yet is shown as exactly that, never as complete on the strength of a timer.
							</p>
						</>
					),
					limits: (
						<>
							<p>
								<strong>A topic is not a window.</strong> An ended window does not close its
								subject, and a window that touched two subjects does not merge them. A topic with no
								records in a section says so rather than showing a fabricated entry.
							</p>
							<p>
								<strong>There is no progress percentage, and there will not be one.</strong> A
								window has no denominator: how much research is “left” is not a quantity anything
								records. What is shown is what happened — segments run, when the last one ran — and
								a window that is doing nothing looks like a window doing nothing.
							</p>
							<p>
								<strong>Repeated activity is not progress.</strong> A segment that ran and concluded
								nothing is counted separately from one that could not start, and neither is
								reported as advancement. Five minutes of silence is where this screen stops
								calling a lane alive; the record itself says only when the last segment ran.
							</p>
						</>
					),
				}}
			/>

			<Technical summary="State names on the record">
				<ul class="records">
					{STATE_VOCABULARY.map((entry) => (
						<li key={entry.name} class="record">
							<div class="record-head">
								<code>{entry.name}</code>
								{entry.stored.startsWith('NOT A STORED STATE') && (
									<span class="tag tag-warn">no such stored state</span>
								)}
							</div>
							<p class="muted small">{entry.stored}</p>
						</li>
					))}
				</ul>
			</Technical>

			{/* ==================================================== THE DRIVER
			    WHAT IS WAKING THE WINDOW, READ FROM THE RECORD. Every line is a
			    read: the Durable Object's own `getAlarm()`, the tick ledger, and
			    the run row. Nothing here infers a schedule, and an unreadable
			    alarm is said to be unreadable rather than healthy. */}
			<section class="panel" aria-labelledby="driver-heading">
				<div class="topic-section-head">
					<h2 id="driver-heading">Research driver</h2>
					{driverRead.status === 'loading' && <span class="muted small">reading…</span>}
				</div>
				{driverSnapshot === null ? (
					<p class="muted">
						{driverRead.status === 'failed'
							? 'The driver status could not be read. This says nothing about whether a driver is running.'
							: 'No driver telemetry has been read yet.'}
					</p>
				) : driverSnapshot.available === false ? (
					<p class="muted">
						{driverSnapshot.reason ??
							'This deployment cannot observe its research driver from here.'}
					</p>
				) : (
					<>
						<ul class="records">
							<li class="record">
								<div class="record-head">
									<span>Driver implemented in this build</span>
									<span class={`tag ${driverSnapshot.driverImplemented === true ? 'tag-live' : ''}`}>
										{driverSnapshot.driverImplemented === true ? 'YES' : 'NOT REPORTED'}
									</span>
								</div>
								<p class="muted small">
									Scheduler binding: {driverSnapshot.schedulerBindingPresent === true ? 'present' : 'absent'} ·
									scheduled handler: {driverSnapshot.scheduledHandlerImplemented === true ? 'present' : 'not reported'} ·
									monitoring: {driverSnapshot.monitoringAvailable === true ? 'available' : 'not reported'}.
								</p>
							</li>
							<li class="record">
								<div class="record-head">
									<span>Trigger and alarm</span>
									<span class="tag">{driverSnapshot.levels?.triggerObserved === true ? 'TRIGGER OBSERVED' : 'NO CRON TICK OBSERVED'}</span>
								</div>
								<p class="muted small">
									Last CRON-origin tick: {driverSnapshot.cronTriggerObservedAt === null || driverSnapshot.cronTriggerObservedAt === undefined
										? 'none observed — the Worker cannot read whether a platform trigger is configured'
										: formatRecordTime(driverSnapshot.cronTriggerObservedAt)}.
									Last ALARM-origin tick: {driverSnapshot.lastAlarmTickAt === null || driverSnapshot.lastAlarmTickAt === undefined
										? 'none recorded'
										: formatRecordTime(driverSnapshot.lastAlarmTickAt)}.
									Next alarm (the object’s own `getAlarm()`): {driverSnapshot.nextAlarmAt === null || driverSnapshot.nextAlarmAt === undefined
										? 'none reported'
										: formatRecordTime(driverSnapshot.nextAlarmAt)}.
								</p>
								{driverSnapshot.alarm?.known === false && (
									<p class="muted small">
										The scheduler object could not be read, so no alarm state is claimed.
									</p>
								)}
							</li>
							<li class="record">
								<div class="record-head">
									<span>Execution</span>
									<span class={`tag ${driverSnapshot.progressBlocked === true ? 'tag-warn' : ''}`}>
										{driverSnapshot.progressBlocked === true ? 'BLOCKED' : 'NO BLOCK REPORTED'}
									</span>
								</div>
								<p class="muted small">
									Ticks recorded: {driverSnapshot.levels?.tickExecuted === true ? 'yes' : 'none'} ·
									segment advanced: {driverSnapshot.levels?.segmentAdvanced === true ? 'yes' : 'not recorded'} ·
									active window: {driverSnapshot.activeRun === null || driverSnapshot.activeRun === undefined
										? 'none'
										: `${driverSnapshot.activeRun.runId} (${driverSnapshot.activeRun.state}, ${driverSnapshot.activeRun.segmentsRun} segment(s))`}.
									{driverSnapshot.blockedReason !== null && driverSnapshot.blockedReason !== undefined && (
										<> Last refusal: {driverSnapshot.blockedReason}</>
									)}
								</p>
							</li>
						</ul>
						{(driverSnapshot.lastTicks ?? []).length > 0 && (
							<Technical summary="Recent scheduler ticks">
								<ul class="records">
									{(driverSnapshot.lastTicks ?? []).slice(0, 8).map((tick) => (
										<li key={tick.tickId} class="record">
											<div class="record-head">
												<code>{tick.origin}</code>
												<span class="muted small">{formatRecordTime(tick.at)}</span>
											</div>
											<p class="muted small">
												{tick.decision} — {tick.reason}
											</p>
										</li>
									))}
								</ul>
							</Technical>
						)}
						{driverSnapshot.note !== undefined && (
							<p class="muted small">{driverSnapshot.note}</p>
						)}
					</>
				)}
			</section>

			{/* ================================================== THE DIRECTORY
			    THE PRIMARY CONTENT IS THE SUBJECTS, NOT THE WINDOWS. The table is
			    an index: a topic title, its own summary, its status and the last
			    meaningful update. A row opens the topic's complete page.
			    ON THE WINDOWS TAB IT IS NOT RENDERED: Research Control owns the
			    directory, and showing it here too would make the second place the
			    one that drifts. */}
			{mode !== 'windows' && (
			<section class="panel" aria-labelledby="topics-heading">
				<div class="topic-section-head">
					<h2 id="topics-heading">All research topics</h2>
					<span class="muted small">
						{topicsState.status === 'ready' && topicsState.data?.wired === true
							? `Showing ${topicList.length} loaded${topicsHasMore ? ' — more exist' : ''}`
							: ''}
					</span>
				</div>
				{topicList.length > 0 && (
					<div class="topic-filters">
						<input
							type="search"
							class="composer-input"
							aria-label="Search research topics"
							placeholder="Search topics…"
							value={topicQuery}
							onInput={(event: Event) => setTopicQuery((event.target as HTMLInputElement).value)}
						/>
						<select
							class="composer-input"
							aria-label="Filter by status"
							value={topicStatus}
							onChange={(event: Event) => setTopicStatus((event.target as HTMLSelectElement).value)}
						>
							<option value="ALL">All statuses</option>
							{topicStatuses.map((status) => (
								<option key={status} value={status}>
									{status}
								</option>
							))}
						</select>
					</div>
				)}
				{topicsState.status === 'loading' && (
					<p class="notice" role="status">Reading the research directory…</p>
				)}
				{topicsState.status === 'failed' && topicsState.error !== null && (
					<Failure error={topicsState.error} what="the research directory" onRetry={() => void topics.load()} />
				)}
				{topicsState.status === 'ready' && topicsState.data !== null && !topicsState.data.wired && (
					<p class="notice">
						<strong>This deployment records no research topics.</strong>{' '}
						{topicsState.data.reason ??
							'That is a deployment gap, not evidence that no subject was researched.'}
					</p>
				)}
				{topicsState.status === 'ready' && topicsState.data?.wired === true && topicList.length === 0 && (
					<p>
						<strong>
							{topicQuery.trim().length > 0 || topicStatus !== 'ALL'
								? 'No research topic matches that search or status.'
								: 'No research topic is on record yet.'}
						</strong>{' '}
						<span class="muted">
							{topicQuery.trim().length > 0 || topicStatus !== 'ALL'
								? 'The filter ran over the whole topic corpus on the server, not over a loaded page.'
								: 'A topic is created when Scout attributes its authored work to a subject. A window that ran but never attributed its work leaves no topic, and that absence is shown honestly rather than filled with a placeholder.'}
						</span>
					</p>
				)}
				{topicList.length > 0 && (
					<div class="topic-table-wrap">
						<table class="topic-table">
							<thead>
								<tr>
									<th>Research topic</th>
									<th>Latest summary</th>
									<th>Status</th>
									<th>Last updated</th>
								</tr>
							</thead>
							<tbody>
								{topicList.map((topic) => (
									<tr
										key={topic.topicId}
										data-topic-id={topic.topicId}
										tabIndex={0}
										role="link"
										onClick={() =>
											navigate({
												destination: 'research',
												section: 'topic',
												recordId: topic.topicId,
											})
										}
										onKeyDown={(event: KeyboardEvent) => {
											if (event.key === 'Enter') {
												navigate({
													destination: 'research',
													section: 'topic',
													recordId: topic.topicId,
												});
											}
										}}
									>
										<td class="topic-title">
											<span class="topic-row-title">{topic.title}</span>
										</td>
										<td class="topic-row-summary">
											{topic.summary.length > 0
												? topic.summary
												: topic.objective}
										</td>
										<td>
											<span class="tag">{topic.status}</span>
										</td>
										<td class="topic-date">
											<time dateTime={topic.lastActivityAt}>
												{formatRecordTime(topic.lastActivityAt)}
											</time>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				<p class="muted small">
					This directory is a page of the topic record, not the whole corpus. Search and status
					run over the whole topic corpus on the server; the control below asks for the next page
					of the same ordered read.
				</p>
				{topicsHasMore && (
					<button
						type="button"
						class="button ghost"
						disabled={topicsPaging}
						onClick={() => void loadMoreTopics()}
					>
						{topicsPaging ? 'Reading the next page…' : 'Load more topics'}
					</button>
				)}
				{topicsPageError !== null && (
					<p class="notice small" role="status">
						The next page of topics could not be read: {topicsPageError.message}
					</p>
				)}
			</section>
			)}

			<section class="panel" aria-labelledby="windows-heading">
				<h2 id="windows-heading">Research windows — authorizations and execution history</h2>
				<p class="muted small">
					A window records WHEN and FOR HOW LONG you permitted autonomous work. It is not a research
					topic, and an ended window does not close the subject it worked on.
				</p>
			</section>

			{state.status === 'loading' && (
				<p class="notice" role="status">
					Reading Scout’s research windows…
				</p>
			)}
			{state.status === 'failed' && state.error !== null && (
				<Failure error={state.error} what="the research windows" onRetry={() => void runs.load()} />
			)}

			{state.status === 'ready' && data !== null && !data.wired && (
				<p class="notice">
					<strong>This deployment runs no research windows.</strong>{' '}
					{data.reason ?? 'The window service is not wired here, so there is nothing to show — which is not the same as a store with no windows in it.'}
				</p>
			)}

			{state.status === 'ready' && data !== null && data.wired && (
				<>
					{/* ------------------------------------------------ what is open */}
					<section class="panel" aria-labelledby="open-heading">
						<h2 id="open-heading">Open now</h2>
						{/* THE WHOLE SCREEN'S STATE IN ONE LINE, BEFORE THE LIST. It is
						    the answer to "is anything happening" that a reader would
						    otherwise have to assemble from every card below — and it
						    cannot be read as a summary of quality, because the
						    vocabulary has no such word in it. */}
						<ActivityMark reading={overall} subject="All windows" />
						{live.length === 0 ? (
							<p>
								<strong>No research window is open.</strong>{' '}
								<span class="muted">
									Between windows Scout is not researching, and this screen will not imply
									otherwise.
								</span>
							</p>
						) : (
							<ul class="records">
								{live.map((run) => (
									<RunCard
										key={run.runId}
										run={run}
										observedAtMs={observedAtMs}
										open={openRunId === run.runId}
										episodes={openRunId === run.runId ? episodes : null}
										episodeError={openRunId === run.runId ? episodeError : null}
										episodeHasMore={openRunId === run.runId ? episodesMore : false}
										episodePaging={openRunId === run.runId ? episodesPaging : false}
										episodePageError={openRunId === run.runId ? episodesPageError : null}
										journal={openRunId === run.runId ? journal : null}
										journalError={openRunId === run.runId ? journalError : null}
										cancelSent={cancelSent[run.runId] === true}
										cancelError={cancelError?.runId === run.runId ? cancelError.error : null}
										onOpen={() => void openRun(run.runId)}
										onLoadMoreEpisodes={() => void loadMoreRunEpisodes(run.runId)}
										onCancel={() => void cancel(run.runId)}
									/>
								))}
							</ul>
						)}
						<p class="muted small">
							This screen re-reads every {POLL_MS / 1_000} seconds while the tab is visible, and
							stops when it is hidden. Every read here is a plain GET that runs no model, so
							leaving this page open costs nothing.
						</p>
					</section>

					{/* --------------------------------------------------- the past */}
					<section class="panel" aria-labelledby="ended-heading">
						<h2 id="ended-heading">Earlier windows</h2>
						{ended.length === 0 ? (
							<p>
								<strong>No window has ended yet.</strong>{' '}
								<span class="muted">
									An ended window keeps its report, and this is where it appears.
								</span>
							</p>
						) : (
							<ul class="records">
								{ended.map((run) => (
									<RunCard
										key={run.runId}
										run={run}
										observedAtMs={observedAtMs}
										open={openRunId === run.runId}
										episodes={openRunId === run.runId ? episodes : null}
										episodeError={openRunId === run.runId ? episodeError : null}
										episodeHasMore={openRunId === run.runId ? episodesMore : false}
										episodePaging={openRunId === run.runId ? episodesPaging : false}
										episodePageError={openRunId === run.runId ? episodesPageError : null}
										journal={openRunId === run.runId ? journal : null}
										journalError={openRunId === run.runId ? journalError : null}
										cancelSent={false}
										cancelError={null}
										onOpen={() => void openRun(run.runId)}
										onLoadMoreEpisodes={() => void loadMoreRunEpisodes(run.runId)}
										onCancel={() => undefined}
									/>
								))}
							</ul>
						)}
						<p class="muted small">
							Every window that has ended is also on{' '}
							<a
								href={pathForDestination('history')}
								onClick={(event) => {
									event.preventDefault();
									navigate({ destination: 'history', section: null, recordId: null });
								}}
							>
								History
							</a>
							, alongside the conversations and the usage window.
						</p>
					</section>
				</>
			)}
		</section>
	);
}

/* ------------------------------------------------------------- one window */

function RunCard({
	run,
	observedAtMs,
	open,
	episodes,
	episodeError,
	episodeHasMore,
	episodePaging,
	episodePageError,
	journal,
	journalError,
	cancelSent,
	cancelError,
	onOpen,
	onLoadMoreEpisodes,
	onCancel,
}: {
	run: ResearchRun;
	observedAtMs: number;
	open: boolean;
	episodes: Episode[] | null;
	episodeError: GatewayError | null;
	episodeHasMore: boolean;
	episodePaging: boolean;
	episodePageError: GatewayError | null;
	journal: JournalEntry[] | null;
	journalError: GatewayError | null;
	cancelSent: boolean;
	cancelError: GatewayError | null;
	onOpen: () => void;
	onLoadMoreEpisodes: () => void;
	onCancel: () => void;
}) {
	const display = displayStateOf(run, observedAtMs);
	const lastSegmentMs = run.lastSegmentAt === null ? null : Date.parse(run.lastSegmentAt);
	const quietForMs = lastSegmentMs === null ? null : observedAtMs - lastSegmentMs;
	// ALIVE IS A CONCLUSION, NOT A SPINNER. It is stated with the number it was
	// drawn from, so a reader can disagree with it.
	const quiet = quietForMs !== null && quietForMs > QUIET_AFTER_MS;

	return (
		<li class="record">
			<div class="record-head">
				<span class="record-title">Research window</span>
				<span class={`tag${display === 'RUNNING' ? ' tag-live' : ''}`}>{display}</span>
				{run.cancelRequested && <span class="tag tag-warn">stop recorded</span>}
			</div>
			<p class="small">{DISPLAY_CAPTIONS[display]}</p>

			{/* THE OWNER'S OWN WORDS, VERBATIM. Not a paraphrase of the request —
			    the authorization itself, which is what the row stores. */}
			<p class="record-summary">
				you said: <em>{run.authorizationText}</em>
			</p>

			<dl class="facts">
				<div>
					<dt>Authorized</dt>
					<dd>{minutes(run.requestedMs)} — your window, recorded when it opened</dd>
				</div>
				<div>
					<dt>Opened</dt>
					<dd>
						<time dateTime={run.startedAt}>{run.startedAt}</time>
					</dd>
				</div>
				<div>
					<dt>Deadline</dt>
					<dd>
						<time dateTime={run.deadlineAt}>{run.deadlineAt}</time>
					</dd>
				</div>
				<div>
					<dt>Remaining</dt>
					{/* MEASURED AGAINST THE SERVER'S OWN ANSWER TIME, so a wrong device
					    clock cannot misreport a deadline the Owner might act on. */}
					<dd>{remainingText(run.deadlineAt, observedAtMs)}</dd>
				</div>
				<div>
					<dt>Segments</dt>
					<dd>
						<span class="count">{run.segmentsRun}</span> ran ·{' '}
						<span class="count">{run.unrecoverableSegments}</span> could not start ·{' '}
						<span class="count">{run.drySegments}</span> ran and added nothing new
					</dd>
				</div>
				<div>
					<dt>Last segment</dt>
					<dd>
						{run.lastSegmentAt === null ? (
							<span class="muted">none has run yet</span>
						) : (
							<>
								<time dateTime={run.lastSegmentAt}>{run.lastSegmentAt}</time>
								<span class="muted small">
									{' '}
									· {quiet ? `quiet for ${minutes(quietForMs ?? 0)}` : 'recent'}
								</span>
							</>
						)}
					</dd>
				</div>
				{run.endReason !== null && (
					<div>
						<dt>Ended</dt>
						<dd>
							<code>{run.endReason}</code>
							{run.terminalReason !== null && (
								<span class="muted small"> — {run.terminalReason}</span>
							)}
						</dd>
					</div>
				)}
			</dl>

			<Technical summary="Window identifier">
				<p class="small">
					<code>{run.runId}</code>
					{run.conversationId !== null && (
						<>
							{' '}
							· asked in <code>{run.conversationId}</code>
						</>
					)}
				</p>
			</Technical>

			{display === 'RUNNING' && quiet && (
				<p class="notice small">
					<strong>This window is open and has recorded nothing for {minutes(quietForMs ?? 0)}.</strong>{' '}
					That is what the record says. A segment may be running and take as long as it takes; a
					lane that has genuinely stopped will end at the deadline either way. This screen will not
					guess which it is.
				</p>
			)}

			{/* THE ACTIVITY MARK, FROM THE SAME DERIVATION THIS CARD ALREADY USES.
			    It is here rather than only at the top of the screen because the
			    Owner's question — "is anything happening" — is asked while
			    looking at ONE window, and the `moving` flag is what stops the
			    ring turning on a window whose lane has gone quiet. */}
			<ActivityMark reading={runActivity(run, observedAtMs)} />

			<p>
				<button type="button" class="button ghost" aria-expanded={open} onClick={onOpen}>
					{open ? 'Hide what it did' : 'What is it investigating?'}
				</button>
			</p>

			{open && (
				<div class="archive-pane">
					{/* ------------------------------------------ SCOUT'S OWN WORDS
					    THE JOURNAL COMES FIRST, because it is the answer to "what
					    did Scout actually research". The execution records below
					    support it; the deterministic report is demoted to a
					    technical block at the end. */}
					<h3 class="subhead">Scout’s research journal</h3>
					{journalError !== null ? (
						<Failure error={journalError} what="Scout’s authored journal" onRetry={onOpen} />
					) : journal === null ? (
						<p class="notice" role="status">
							Reading Scout’s journal…
						</p>
					) : journal.length === 0 ? (
						<p class="muted small">
							<strong>Scout has not written an entry for this window.</strong>{' '}
							<span class="muted">
								A journal entry is authored at the opening, at a substantive progress point, and
								in the synthesis reserve. An empty journal is a statement about the authorship
								moments this window reached — not evidence that no research happened; the record
								below says what the lane did.
							</span>
						</p>
					) : (
						<ol class="journal">
							{journal.map((entry) => (
								<li key={entry.journalId} class="record journal-entry">
									<div class="record-head">
										<span class="tag">
											{entry.kind === 'OPENING'
												? 'Opening'
												: entry.kind === 'PROGRESS'
													? 'Progress'
													: entry.kind === 'CONCLUSION'
														? 'Scout’s conclusion'
														: 'Final synthesis'}
										</span>
										<span
											class={`tag${entry.author === 'SCOUT' ? '' : ' tag-warn'}`}
										>
											{entry.author === 'SCOUT'
												? 'Scout wrote this'
												: 'Harness note — not Scout’s words'}
										</span>
										<time class="muted small" dateTime={entry.createdAt}>
											{entry.createdAt}
										</time>
									</div>
									{entry.title !== null && <h4 class="record-title">{entry.title}</h4>}
									{entry.content.length > 0 ? (
										<Markdown content={entry.content} class="turn-markdown authored-content" />
									) : null}
									{entry.status !== 'AUTHORED' && entry.statusNote !== null && (
										<p class="notice small" role="status">
											<strong>
												{entry.status === 'FAILED'
													? 'This entry was not written.'
													: 'This entry is incomplete.'}
											</strong>{' '}
											{entry.statusNote}
										</p>
									)}
									{/* A CONCLUSION IS AUTHORED, BUT IT IS NOT A SYNTHESIS. The
									    note says where the words came from, so the absence of a
									    separate final synthesis is stated rather than inferred. */}
									{entry.kind === 'CONCLUSION' &&
										entry.status === 'AUTHORED' &&
										entry.statusNote !== null && (
											<p class="muted small">{entry.statusNote}</p>
										)}
									{entry.evidenceRefs.length > 0 && (
										<Technical summary={`${entry.evidenceRefs.length} evidence reference(s)`}>
											<ul class="references">
												{entry.evidenceRefs.map((ref) => (
													<li key={ref}>
														<code>{ref}</code>
													</li>
												))}
											</ul>
										</Technical>
									)}
								</li>
							))}
						</ol>
					)}

					{/* ------------------------------------- WHAT THE RECORD HOLDS
					    The execution record supports the journal; it is not a
					    substitute for it and it is not hidden when empty. */}
					<h3 class="subhead">What this window recorded</h3>
					{episodeError !== null ? (
						<Failure error={episodeError} what="this window’s investigations" onRetry={onOpen} />
					) : episodes === null ? (
						<p class="notice" role="status">
							Reading this window’s investigations…
						</p>
					) : episodes.length === 0 ? (
						// THE HONEST EMPTY. A window that has produced no investigation
						// has either not had a segment yet or had one that could not
						// start — and the counts above say which.
						<p class="muted small">
							<strong>This window has recorded no investigation.</strong> That is either a window
							whose first segment has not run, or one whose segments could not start — the
							segment counts above say which. Nothing is inferred from the silence.
						</p>
					) : (
						<>
							<ul class="records">
								{episodes.map((episode) => (
								<li key={episode.episodeId} class="record">
									<div class="record-head">
										<span class="record-title">{episode.question}</span>
										<span class="tag">{episode.status}</span>
									</div>
									<p class="muted small">
										opened <time dateTime={episode.createdAt}>{episode.createdAt}</time>
										{typeof episode.sourceCount === 'number' && (
											<> · {episode.sourceCount} source(s)</>
										)}
										{typeof episode.findingCount === 'number' && (
											<> · {episode.findingCount} finding(s)</>
										)}
									</p>
									<Technical summary="Investigation identifier">
										<code>{episode.episodeId}</code>
									</Technical>
								</li>
							))}
						</ul>
						{episodeHasMore && (
							<button
								type="button"
								class="button ghost"
								disabled={episodePaging}
								onClick={onLoadMoreEpisodes}
							>
								{episodePaging ? 'Reading the next page…' : 'Load more investigations'}
							</button>
						)}
						{episodePageError !== null && (
							<p class="notice small" role="status">
								The next page of investigations could not be read:{' '}
								{episodePageError.message}
							</p>
						)}
						</>
					)}
				</div>
			)}

			{/* THE REPORT IS THE HARNESS'S OWN METADATA, NOT SCOUT'S NARRATIVE.
			    It is deterministic and checkable, and the journal above is the
			    authored account. Putting the report in an expandable technical
			    block keeps it readable without letting a counter dump stand in
			    for the science. */}
			{run.report !== null && (
				<Technical summary="Harness execution report — deterministic metadata, not Scout’s words">
					<blockquote class="report">{run.report}</blockquote>
				</Technical>
			)}
			{run.report === null && run.state !== 'RUNNING' && (
				<p class="muted small">
					This window’s report is not in this read. Reports are rendered for the most recent ended
					windows, and an older one is readable through its own reference
					{run.reportRef !== null && (
						<>
							{' '}
							(<code>{run.reportRef}</code>)
						</>
					)}
					.
				</p>
			)}

			{run.state === 'RUNNING' && (
				<>
					<p>
						<button
							type="button"
							class="button"
							disabled={run.cancelRequested || cancelSent}
							onClick={onCancel}
						>
							{run.cancelRequested || cancelSent ? 'Stop recorded' : 'Stop this window'}
						</button>
					</p>
					{/* WHAT THE BUTTON DOES, BEFORE IT IS PRESSED. The button does not
					    stop anything: it records the Owner's word, and the next tick
					    ends the window through the path the deadline takes. */}
					<p class="muted small">
						Stopping records your instruction. The window ends at its next tick and starts no
						further segment. A segment already running may finish what it is doing — a model call
						or a retrieval already in flight is not something this can recall — and anything it
						records is kept.
					</p>
					{cancelError !== null && (
						<p class="small" role="alert">
							<strong>Not recorded.</strong> Scout did not accept the stop:{' '}
							<code>{cancelError.name}</code> — {cancelError.message}
						</p>
					)}
				</>
			)}
		</li>
	);
}
